import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDocuments, type DocumentRow } from "@/lib/queries";
import { useDeleteDocument } from "@/lib/mutations";
import { apiFetch } from "@/lib/api";
import { fmtDate } from "@emr/shared/india";
import { Download, Trash, FileText, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UploadFiles } from "@/components/UploadFiles";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  patientId: string;
  visitId?: string;
}

/**
 * Documents dialog for a patient (optionally visit-scoped).
 *
 * Lists ALL documents uploaded for the patient (visit-linked and patient-level),
 * with preview / download / delete, and an inline upload box (PDF-only policy,
 * images auto-converted to compressed 72-DPI PDF — see lib/pdf/convertToPdf.ts).
 */
export function DocumentViewer({ open, onOpenChange, patientId, visitId }: Props) {
  // Always list every document for the patient; visitId is only used to tag uploads.
  const { data, isLoading, refetch } = useDocuments(patientId);
  const docs = data?.rows ?? [];
  const deleteMut = useDeleteDocument();
  const { isAdmin, permissions } = useAuth();
  const canUpload = isAdmin || permissions.has("documents.create");
  const canDelete = isAdmin || permissions.has("documents.delete");
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewMime, setPreviewMime] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const download = async (doc: DocumentRow) => {
    setDownloading(doc.id);
    try {
      const res = await apiFetch<{ url: string; fileName: string; mimeType: string | null }>(`/documents/${doc.id}/download`);
      const a = document.createElement("a");
      a.href = res.url;
      a.download = res.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error("Failed to download");
    } finally {
      setDownloading(null);
    }
  };

  const preview = async (doc: DocumentRow) => {
    setDownloading(doc.id);
    try {
      const res = await apiFetch<{ url: string; fileName: string; mimeType: string | null }>(`/documents/${doc.id}/download`);
      setPreviewUrl(res.url);
      setPreviewMime(res.mimeType ?? doc.mimeType);
    } catch {
      toast.error("Failed to load preview");
    } finally {
      setDownloading(null);
    }
  };

  const isPdf = (mime: string | null) => mime === "application/pdf";
  const isImage = (mime: string | null) => mime?.startsWith("image/") ?? false;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setPreviewUrl(null); setPreviewMime(null); setUploadOpen(false); } }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-maroon-700 dark:text-amber-500">Documents</DialogTitle>
          <DialogDescription>
            {visitId ? "All documents uploaded for the patient" : "Documents for patient"}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-3 p-1">
            {canUpload && (
              <div className="rounded-lg border border-cream-200 p-3 dark:border-dark-700">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-medium text-ink dark:text-cream-50">Add documents</div>
                  <Button size="sm" variant="ghost" onClick={() => setUploadOpen((v) => !v)}>
                    {uploadOpen ? "Hide" : "Add files"}
                  </Button>
                </div>
                {uploadOpen && (
                  <UploadFiles
                    patientId={patientId}
                    visitId={visitId}
                    onUploaded={() => { refetch(); setUploadOpen(false); }}
                  />
                )}
              </div>
            )}

            {isLoading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
            ) : docs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted">No documents.</div>
            ) : (
              docs.map((doc) => (
                <div key={doc.id} className="flex items-center gap-3 rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                    <FileText size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink dark:text-cream-50">{doc.fileName}</div>
                    <div className="truncate text-xs text-muted">
                      {doc.type ?? "Document"} · {fmtDate(doc.documentDate)}
                      {doc.description && ` · ${doc.description}`}
                      {visitId && doc.visitId && doc.visitId !== visitId && " · from an earlier visit"}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {(isImage(doc.mimeType) || isPdf(doc.mimeType)) && (
                      <Button size="sm" variant="ghost" onClick={() => preview(doc)} disabled={downloading === doc.id}>
                        {downloading === doc.id ? <LoaderCircle size={14} className="animate-spin" /> : "Preview"}
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => download(doc)} disabled={downloading === doc.id}>
                      <Download size={14} />
                    </Button>
                    {canDelete && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => deleteMut.mutate(doc.id)}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}

            {previewUrl && (
              <div className="mt-3 rounded-lg border border-cream-200 p-2 dark:border-dark-700">
                {isImage(previewMime) ? (
                  <img src={previewUrl} alt="Preview" className="max-h-[50vh] w-full object-contain" />
                ) : isPdf(previewMime) ? (
                  <iframe src={previewUrl} className="h-[50vh] w-full" title="PDF preview" />
                ) : (
                  <div className="p-4 text-sm text-muted">Preview not available for this file type.</div>
                )}
                <Button size="sm" variant="ghost" className="mt-2" onClick={() => { setPreviewUrl(null); setPreviewMime(null); }}>Close preview</Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
